import React, { useState, useRef } from 'react';
import axios from 'axios';
import './PDFUploader.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MAX_FILES = 10;

const TextractUploader = ({ onUploadSuccess }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const abortControllerRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length === 0) return;

    const pdfs = selected.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    const nonPdfs = selected.filter(f => !f.name.toLowerCase().endsWith('.pdf'));

    if (nonPdfs.length > 0) {
      setError(`Skipped non-PDF files: ${nonPdfs.map(f => f.name).join(', ')}`);
    } else {
      setError(null);
    }

    if (pdfs.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed. Only the first ${MAX_FILES} will be used.`);
      pdfs.splice(MAX_FILES);
    }

    if (pdfs.length > 0) {
      setFiles(pdfs);
      setResults([]);
      if (!isExpanded) setIsExpanded(true);
    }
  };

  const handleCancel = () => {
    setCancelling(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one PDF file');
      return;
    }

    setUploading(true);
    setCancelling(false);
    setError(null);
    setResults([]);
    if (!isExpanded) setIsExpanded(true);

    abortControllerRef.current = new AbortController();

    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));

      const response = await axios.post(
        `${API_BASE_URL}/insurance-documents/batch-extract-pdf`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 900000, // 15 minutes
          signal: abortControllerRef.current.signal,
        }
      );

      const data = response.data;
      setResults(data.results || []);
      setFiles([]);

      const fileInput = document.getElementById('textract-upload');
      if (fileInput) fileInput.value = '';

      if (onUploadSuccess && data.success_count > 0) {
        onUploadSuccess();
      }

      setTimeout(() => {
        setResults([]);
      }, 10000);

    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        setError('Extraction cancelled by user');
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Request timed out. The extraction may still be running on the server.');
      } else if (err.response?.data?.detail) {
        setError(`Extraction failed: ${err.response.data.detail}`);
      } else {
        setError(`Extraction failed: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setUploading(false);
      setCancelling(false);
      abortControllerRef.current = null;
    }
  };

  const getTotalSize = () => {
    const bytes = files.reduce((sum, f) => sum + f.size, 0);
    if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  return (
    <div className="pdf-uploader">
      <div
        className="upload-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          cursor: 'pointer',
          background: 'linear-gradient(to right, #fef3c7, #fed7aa)',
          borderBottom: '2px solid #f59e0b',
        }}
      >
        <div className="upload-header-content">
          <div>
            <h2 style={{ color: '#92400e' }}>AWS PDF Extractor</h2>
            <p className="upload-subtitle" style={{ color: '#a16207' }}>
              Extract markdown from up to {MAX_FILES} PDFs using AWS Textract
            </p>
          </div>
          <div className={`chevron-icon ${isExpanded ? 'expanded' : ''}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <div className={`upload-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="file-input-wrapper">
          <input
            type="file"
            id="textract-upload"
            accept=".pdf"
            onChange={handleFileChange}
            disabled={uploading}
            className="file-input"
            multiple
          />
          <label
            htmlFor="textract-upload"
            className="file-label"
            style={{ borderColor: '#f59e0b' }}
          >
            <div className="file-label-content">
              <div className="file-icon" style={{ color: '#d97706' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.27 6.96L12 12.01l8.73-5.05" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 22.08V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="file-label-text">
                {files.length > 0 ? (
                  <>
                    <span className="file-name">{files.length} PDF{files.length > 1 ? 's' : ''} selected</span>
                    <span className="file-size">Total: {getTotalSize()}</span>
                    <div className="selected-files-list">
                      {files.slice(0, 5).map((file, idx) => (
                        <span key={idx} className="selected-file-item">{file.name}</span>
                      ))}
                      {files.length > 5 && (
                        <span className="selected-file-item">...and {files.length - 5} more</span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="file-label-main">Click to browse or drag and drop</span>
                    <span className="file-label-hint">Select up to {MAX_FILES} PDF files</span>
                  </>
                )}
              </div>
              {files.length === 0 && (
                <div
                  className="file-browse-button"
                  style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}
                >
                  Browse Files
                </div>
              )}
            </div>
          </label>
        </div>

        {uploading ? (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="upload-button cancel-upload-button"
          >
            {cancelling ? (
              <>
                <span className="button-spinner"></span>
                Cancelling...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Cancel Extraction
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleUpload}
            disabled={files.length === 0}
            className="upload-button"
            style={files.length > 0 ? { background: 'linear-gradient(135deg, #d97706, #b45309)' } : {}}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Extract {files.length > 0 ? `${files.length} PDF${files.length > 1 ? 's' : ''}` : 'PDFs'}
          </button>
        )}
      </div>

      {/* Loading indicator while extracting */}
      {uploading && (
        <div className="loading-container">
          <div className="spinner" style={{ borderTopColor: '#d97706' }}></div>
          <p>Extracting {files.length} PDF{files.length > 1 ? 's' : ''} with AWS Textract... This may take several minutes.</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">&#10060;</span>
          <span>{error}</span>
        </div>
      )}

      {results.length > 0 && (
        <div className="success-container" style={errorCount > 0 && successCount === 0 ? { backgroundColor: 'var(--red-50)', borderLeftColor: 'var(--red-600)' } : {}}>
          <div className="success-header">
            <span className="success-icon">{errorCount === 0 ? '\u2705' : successCount > 0 ? '\u26a0\ufe0f' : '\u274c'}</span>
            <h3 style={errorCount > 0 && successCount === 0 ? { color: 'var(--red-800)' } : {}}>
              Extraction Results ({successCount}/{results.length} succeeded)
            </h3>
          </div>
          <div className="upload-results-list">
            {results.map((result, idx) => (
              <div key={idx} className={`upload-result-item ${result.status === 'success' ? 'success' : 'failed'}`}>
                <span className="result-icon">{result.status === 'success' ? '\u2713' : '\u2717'}</span>
                <span className="result-filename">{result.filename}</span>
                {result.status === 'success' ? (
                  <>
                    <span className="result-id">ID: {result.document_id}</span>
                    {result.pages_processed && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginLeft: '0.25rem' }}>
                        {result.pages_processed}p / {result.processing_time_seconds?.toFixed(1)}s
                      </span>
                    )}
                  </>
                ) : (
                  <span className="result-error">{result.error}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TextractUploader;
